import { ApiProductGridLayout, ApiSectionType } from '@storefronts/common-types'
import { ReactNode } from 'react'
import { Accordion } from './Accordion'
import { AiReviewSummarySection } from './AiReviewSummarySection'
import { BodyText } from './BodyText'
import { ButtonSection } from './ButtonSection'
import { CollectionsSection } from './CollectionsSection'
import { HeaderText } from './HeaderText'
import { HTMLSection } from './HTMLSection'
import { InlineProductPageSection } from './InlineProductPageSection'
import { PersonalizedPLPSection } from './PersonalizedPLPSection'
import { PLPSection } from './PLPSection'
import { ProductCardSection } from './ProductCardSection'
import { RecentlyViewedSection } from './RecentlyViewedSection'
import { ReviewSectionSummary } from './ReviewSectionSummary'
import { ReviewsCarousel } from './reviews-carousel/ReviewsCarousel'
import { ReviewsImagesSection } from './ReviewsImagesSection'
import { ReviewsSection } from './ReviewsSection'
import { RichTextHtmlSection } from './RichTextHtmlSection'
import { InlineWrapper, Section } from './SectionShell'
import { SpacerSection } from './SpacerSection'
import { StaticCarousel } from './StaticCarousel'
import { UpsellBox } from './UpsellBox'
import { VideoCarousel } from './video-carousel/VideoCarousel'
import { registerSection, SectionRenderContext } from './sectionRenderers'

// Section components render product UI, and product UI renders sections.
// Registering them here, and importing this module only from page entry
// points, keeps that loop out of the import graph.
const frame = (
    section: Parameters<typeof Section>[0]['section'],
    ctx: SectionRenderContext,
    child: ReactNode,
    options?: { inline?: boolean; backgroundColor?: string },
) => (
    <Section
        section={section}
        index={ctx.index}
        inline={options?.inline}
        backgroundColor={options?.backgroundColor}
        sectionRef={ctx.sectionRef}
        stampInspectAttrs={ctx.stampInspectAttrs}
    >
        {child}
    </Section>
)

const scrollsHorizontally = (layout: string | undefined): boolean =>
    layout === ApiProductGridLayout.HORIZONTAL_SCROLL

registerSection(ApiSectionType.ACCORDION, (section, ctx) => {
    if (!section.items) return null
    return frame(section, ctx, <Accordion section={section} />, {
        backgroundColor: section.backgroundColor,
    })
})

registerSection(ApiSectionType.HTML, (section, ctx) =>
    frame(section, ctx, <HTMLSection section={section} />, { inline: true }),
)

registerSection(ApiSectionType.CAROUSEL, (section, ctx) =>
    frame(
        section,
        ctx,
        <StaticCarousel
            section={section}
            prioritizeImages={ctx.prioritizeImages}
        />,
        { backgroundColor: section.backgroundColor },
    ),
)

registerSection(ApiSectionType.VIDEO_CAROUSEL, (section, ctx) =>
    frame(section, ctx, <VideoCarousel section={section} />, {
        inline: true,
        backgroundColor: section.backgroundColor,
    }),
)

registerSection(ApiSectionType.PLP, (section, ctx) =>
    frame(
        section,
        ctx,
        <PLPSection section={section} isBelowFold={ctx.isBelowFold} />,
        { inline: scrollsHorizontally(section.layoutStyle) },
    ),
)

registerSection(ApiSectionType.PRODUCT_CARD, (section, ctx) =>
    frame(
        section,
        ctx,
        <ProductCardSection section={section} isBelowFold={ctx.isBelowFold} />,
    ),
)

registerSection(ApiSectionType.REVIEWS_SUMMARY, (section, ctx) =>
    frame(section, ctx, <ReviewSectionSummary />),
)

registerSection(ApiSectionType.REVIEWS_SECTION, (section, ctx) => (
    <>
        {ctx.showRecentlyViewed && (
            <InlineWrapper key="recently-viewed-section">
                <RecentlyViewedSection />
            </InlineWrapper>
        )}
        {frame(
            section,
            ctx,
            <ReviewsSection
                section={section}
                compact={ctx.compact}
                productLink={
                    ctx.reviewsProductLink?.linkHash === section.linkHash
                        ? ctx.reviewsProductLink
                        : undefined
                }
            />,
        )}
    </>
))

registerSection(ApiSectionType.HEADER_TEXT, (section, ctx) =>
    frame(section, ctx, <HeaderText section={section} />),
)

registerSection(ApiSectionType.BODY_TEXT, (section, ctx) =>
    frame(section, ctx, <BodyText section={section} />),
)

registerSection(ApiSectionType.UPSELL_BOX, (section, ctx) =>
    frame(
        section,
        ctx,
        <UpsellBox section={section} isBelowFold={ctx.isBelowFold} />,
    ),
)

registerSection(ApiSectionType.INLINE_PDP, (section, ctx) =>
    frame(
        section,
        ctx,
        <InlineProductPageSection
            section={section}
            prioritizeHeroImage={ctx.prioritizeImages}
        />,
        { inline: true },
    ),
)

registerSection(ApiSectionType.PERSONALIZED_PLP, (section, ctx) =>
    frame(
        section,
        ctx,
        <PersonalizedPLPSection
            section={section}
            isBelowFold={ctx.isBelowFold}
        />,
        { inline: scrollsHorizontally(section.layoutStyle) },
    ),
)

registerSection(ApiSectionType.RICH_TEXT, (section, ctx) =>
    frame(
        section,
        ctx,
        <RichTextHtmlSection
            html={section.html}
            sectionTitle={section.sectionTitle}
        />,
        { backgroundColor: section.backgroundColor },
    ),
)

registerSection(ApiSectionType.CTA_BUTTON, (section, ctx) =>
    frame(section, ctx, <ButtonSection section={section} />, {
        inline: !!ctx.compact,
        backgroundColor: section.backgroundColor,
    }),
)

registerSection(ApiSectionType.AI_REVIEWS_SUMMARY, (section, ctx) =>
    frame(section, ctx, <AiReviewSummarySection section={section} />),
)

registerSection(ApiSectionType.REVIEWS_CAROUSEL, (section, ctx) =>
    frame(section, ctx, <ReviewsCarousel section={section} />, {
        inline: true,
    }),
)

registerSection(ApiSectionType.REVIEWS_IMAGES, (section, ctx) =>
    frame(section, ctx, <ReviewsImagesSection section={section} />),
)

registerSection(ApiSectionType.COLLECTIONS_SECTION, (section, ctx) =>
    frame(section, ctx, <CollectionsSection section={section} />, {
        inline: scrollsHorizontally(section.collectionsLayout),
    }),
)

registerSection(ApiSectionType.SPACER, (section, ctx) =>
    frame(section, ctx, <SpacerSection section={section} />, {
        inline: true,
        backgroundColor: section.backgroundColor,
    }),
)
